import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { Zeichen } from "./Zeichen";
import { Knopf } from "./index";
import {
  useGelesen, useGlocke, useMeineMeldungseinstellungen, useMeineMeldungseinstellungenAendern,
} from "../daten/meldungen";
import { glockenSchluessel } from "../daten/meldungen";
import { meldungsweg } from "../daten/nachbereitung";
import { useSitzung } from "../schale/Sitzung";
import { ladestandVon, leersatzFuer } from "../daten/ladestand";
import type { Meldung } from "../daten/meldungen";
import { grundwerte, meldungskopf, mitLaufname, werteMitErsatz } from "../daten/meldungstext";
import { PushAutomatik, PushZeile } from "./PushAutomatik";
import { Pushgeraete, Pushproben } from "./Pushgeraete";

/* ══════════════════════════════════════════════════════════════════
   Die Glocke (24.09.2026, Paket 5, 51-meldungen.md).

   Luca: *„Auch ne Glocke im Konzept? Also oben in der Leiste mit neuen
   Benachrichtigungen."* Sie steht neben Suche und Nachschlagen, über jeder
   Maske, und beantwortet eine andere Frage als die Startseite: nicht „was
   liegt an?", sondern **„was hat sich für mich geändert, seit ich zuletzt
   hingesehen habe?"**

   - Die Zahl zählt Ungelesenes; sie wird rot, sobald etwas Blockiertes
     darunter ist. Bei null steht keine Zahl.
   - Ein Klick auf einen Eintrag liest ihn und führt dorthin, wo er erledigt
     wird. Erledigte Einträge werden grau.
   - Gelesen gilt auf allen Geräten — es steht an der Meldung, nicht im
     Browser. Dieselbe Zahl trägt das App-Symbol (Badging).
   - Unten „Für mich": Push auf diesem Gerät, Mail, Morgenmail, Ruhezeit.

   **Das Blatt hängt am Dokument, nicht am Kopf** (Portal): Die Werkzeugspalte
   schneidet ab, was über sie hinausragt.
   ══════════════════════════════════════════════════════════════════ */

function zeitWort(iso: string, t: ReturnType<typeof useT>): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return t("meldungen.ebenGerade");
  if (min < 60) return t("meldungen.vorMinuten", { zahl: min });
  if (min < 24 * 60) return t("meldungen.vorStunden", { zahl: Math.round(min / 60) });
  return t("meldungen.vorTagen", { zahl: Math.round(min / 1440) });
}

function Eintrag({ m, schliessen }: { m: Meldung; schliessen: () => void }) {
  const t = useT();
  const gehZu = useNavigate();
  const { darfAuf } = useSitzung();
  const gelesen = useGelesen();
  const erledigt = m.resolved_at !== null;
  const titel = meldungskopf(mitLaufname(m.art, m.data, t));
  const werte = mitLaufname(m.art, m.data.werte ?? {}, t);
  const was = m.art === "zugeteilt"
    ? t("meldungen.zugeteilt", { aufgabe: t(`verteilen.art.${werte["aufgabe"] ?? "vorbereitung"}`) })
    : t(`fall.art.${m.art}`, werte);
  return (
    <button
      type="button"
      className={`glocke-eintrag ${m.stufe}${m.read_at ? "" : " ungelesen"}${erledigt ? " erledigt" : ""}`}
      onClick={() => {
        if (!m.read_at) gelesen.mutate([m.id]);
        // `href`: Der Weg kann `?protokoll=` oder `?fotos=` tragen (D-22).
        // Ohne Akte-Recht der Weg für draußen, wo es einen gibt (P8-02).
        const ziel = meldungsweg(m.data, darfAuf);
        if (ziel) void gehZu({ href: ziel });
        schliessen();
      }}
    >
      <span className={`fallpunkt ${m.stufe}`} aria-hidden="true" />
      <span className="glocke-eintrag-leib">
        <b>{was}</b>
        <span>{titel}</span>
        {werte["grund"] ? <span className="leise">„{werte["grund"]}“</span> : null}
        {werte["grund_pfad"] ? <span className="leise">{t(werte["grund_pfad"], werteMitErsatz(t(werte["grund_pfad"]), grundwerte(werte)))}</span> : null}
        <small>
          {zeitWort(m.created_at, t)} · {erledigt ? t("meldungen.erledigt") : t(`fall.stufe.${m.stufe}`)}
        </small>
      </span>
    </button>
  );
}

function FuerMich() {
  const t = useT();
  const { data } = useMeineMeldungseinstellungen(true);
  const aendern = useMeineMeldungseinstellungenAendern();
  const mail = data?.kanaele.find((k) => k.kanal === "mail");
  const push = data?.kanaele.find((k) => k.kanal === "push");
  const ruheVon = push?.ruhe_von?.slice(0, 5) ?? "20:00";
  const ruheBis = push?.ruhe_bis?.slice(0, 5) ?? "06:00";
  /* **Hier stellt man nur die Ruhezeit** (Luca, 24.09.2026). Push und Mail
     setzt die Verwaltung je Person; was hier steht, sagt nur, wie es ist. */
  return (
    <div className="glocke-fuer-mich">
      <b>{t("meldungen.fuerMich")}</b>
      {push?.an === false ? <p className="glocke-hinweis">{t("meldungen.pushAus")}</p> : <PushZeile />}
      <p className="glocke-hinweis">
        {mail?.an === false ? t("meldungen.mailAus") : t("meldungen.mailAn", { adresse: data?.mail ?? "—" })}
      </p>
      <label className="glocke-schalter">
        {t("meldungen.ruhe")}
        <input
          type="time" value={ruheVon} aria-label={t("meldungen.ruheVon")}
          onChange={(e) => aendern.mutate({ ruhe_von: e.target.value, ruhe_bis: ruheBis })}
        />
        –
        <input
          type="time" value={ruheBis} aria-label={t("meldungen.ruheBis")}
          onChange={(e) => aendern.mutate({ ruhe_von: ruheVon, ruhe_bis: e.target.value })}
        />
      </label>
      <b className="glocke-unter">{t("meldungen.geraete.titelIch")}</b>
      <Pushgeraete geraete={data ? data.geraete ?? [] : undefined} />
      <Pushproben />
      <p className="glocke-hinweis leise">{t("meldungen.vonVerwaltung")}</p>
    </div>
  );
}

/* ══════════════ Das Einfliegen (Luca, 24.09.2026) ══════════════

   *„Die Glocke kommt nicht einfach rein, das muss ja auch reinfliegen und auf
   sich aufmerksam machen."* Was **neu** ungelesen dazukommt, fliegt oben
   rechts als Karte ein, die Glocke wackelt, und ein kurzer, leiser Ton sagt
   es auch dem, der gerade woanders hinsieht. Eine Karte geht nach zehn
   Sekunden von selbst — außer bei „blockiert": die bleibt, bis jemand sie
   wegklickt. Was beim Öffnen der Anwendung schon da war, fliegt nicht: Das
   ist kein Ereignis, das steht in der Glocke und auf der Startseite. */

function ton(laut: boolean) {
  try {
    const Ctx = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const noten = laut ? [880, 660, 880] : [740, 988];
    noten.forEach((hz, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = hz;
      const start = ctx.currentTime + i * 0.13;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.08, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      o.connect(g).connect(ctx.destination);
      o.start(start);
      o.stop(start + 0.25);
    });
    window.setTimeout(() => void ctx.close(), 1200);
  } catch {
    /* Ohne Ton geht es auch — die Karte fliegt trotzdem. */
  }
}

function Anflug({ m, weg }: { m: Meldung; weg: () => void }) {
  const t = useT();
  const gehZu = useNavigate();
  const { darfAuf } = useSitzung();
  const gelesen = useGelesen();
  useEffect(() => {
    if (m.stufe === "blockiert") return;
    const zeit = window.setTimeout(weg, 10_000);
    return () => window.clearTimeout(zeit);
  }, [m.stufe, weg]);
  const werte = mitLaufname(m.art, m.data.werte ?? {}, t);
  const was = m.art === "zugeteilt"
    ? t("meldungen.zugeteilt", { aufgabe: t(`verteilen.art.${werte["aufgabe"] ?? "vorbereitung"}`) })
    : t(`fall.art.${m.art}`, werte);
  const titel = meldungskopf(mitLaufname(m.art, m.data, t));
  return (
    <div className={`glocke-anflug ${m.stufe}`} role="status">
      <button
        type="button" className="glocke-anflug-leib"
        onClick={() => {
          gelesen.mutate([m.id]);
          const ziel = meldungsweg(m.data, darfAuf);
          if (ziel) void gehZu({ href: ziel });
          weg();
        }}
      >
        <span className={`fallpunkt ${m.stufe}`} aria-hidden="true" />
        <span>
          <b>{was}</b>
          {titel ? <span>{titel}</span> : null}
          <small>{t(`fall.stufe.${m.stufe}`)}</small>
        </span>
      </button>
      <button type="button" className="glocke-anflug-zu" aria-label={t("allgemein.schliessen")} onClick={weg}>×</button>
    </div>
  );
}

export function Glocke() {
  const t = useT();
  const meldungen = useGlocke();
  const { data } = meldungen;
  const gelesen = useGelesen();
  const [offen, setzeOffen] = useState(false);
  const [fuerMich, setzeFuerMich] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const blatt = useRef<HTMLDivElement>(null);
  const [platz, setzePlatz] = useState<{ top: number; right: number }>({ top: 60, right: 16 });

  const ungelesen = data?.ungelesen ?? 0;
  const gehZu = useNavigate();
  const abfragen = useQueryClient();

  /* Was neu dazukommt, fliegt ein. `gesehen` ist erst nach der ersten
     Antwort gefüllt — der Bestand beim Öffnen fliegt nicht. */
  const gesehen = useRef<Set<string> | null>(null);
  const [anfluege, setzeAnfluege] = useState<Meldung[]>([]);
  const [wackelt, setzeWackelt] = useState(false);
  useEffect(() => {
    if (!data) return;
    if (gesehen.current === null) {
      gesehen.current = new Set(data.eintraege.map((m) => m.id));
      return;
    }
    const neu = data.eintraege.filter((m) => !gesehen.current!.has(m.id) && !m.read_at && !m.resolved_at);
    data.eintraege.forEach((m) => gesehen.current!.add(m.id));
    if (!neu.length) return;
    setzeAnfluege((alt) => [...neu.slice(0, 3), ...alt].slice(0, 4));
    setzeWackelt(true);
    window.setTimeout(() => setzeWackelt(false), 1200);
    ton(neu.some((m) => m.stufe === "blockiert"));
  }, [data]);

  /* Ein getippter Push führt in das schon offene Fenster (`public/sw.js`,
     `notificationclick`): Der Service Worker schickt den Weg, hier wird
     navigiert — ohne neu zu laden, damit die Anmeldung bleibt. */
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const hoeren = (e: MessageEvent) => {
      const d = e.data as { typ?: string; weg?: string } | null;
      if (d?.typ === "mo-weg" && typeof d.weg === "string") void gehZu({ href: d.weg });
      /* Ein Push ist angekommen: die Glocke sofort nachholen. */
      if (d?.typ === "mo-neu") void abfragen.invalidateQueries({ queryKey: glockenSchluessel });
    };
    sw.addEventListener("message", hoeren);
    return () => sw.removeEventListener("message", hoeren);
  }, [gehZu, abfragen]);

  /* Die Zahl auch am App-Symbol (installierte App). Wo der Browser es nicht
     kann, passiert nichts. */
  useEffect(() => {
    const n = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (ungelesen > 0) void n.setAppBadge?.(ungelesen).catch(() => {});
    else void n.clearAppBadge?.().catch(() => {});
  }, [ungelesen]);

  useLayoutEffect(() => {
    if (!offen || !knopf.current) return;
    const r = knopf.current.getBoundingClientRect();
    setzePlatz({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) });
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const weg = (e: MouseEvent) => {
      if (blatt.current?.contains(e.target as Node) || knopf.current?.contains(e.target as Node)) return;
      setzeOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeOffen(false); };
    document.addEventListener("mousedown", weg);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", weg); document.removeEventListener("keydown", esc); };
  }, [offen]);

  const heute = new Date().toDateString();
  const eintraege = data?.eintraege ?? [];
  const jetzt = eintraege.filter((m) => new Date(m.created_at).toDateString() === heute);
  const frueher = eintraege.filter((m) => new Date(m.created_at).toDateString() !== heute);

  return (
    <>
      {/* Push fragt von selbst — einmal, beim ersten Tipp (24.09.2026). */}
      <PushAutomatik />
      {anfluege.length ? createPortal(
        <div className="glocke-anfluege" aria-live="polite">
          {anfluege.map((m) => (
            <Anflug key={m.id} m={m} weg={() => setzeAnfluege((alt) => alt.filter((x) => x.id !== m.id))} />
          ))}
        </div>,
        document.body,
      ) : null}
      <button
        ref={knopf}
        type="button"
        className={`kopf-nachschlagen glocke-knopf${wackelt ? " wackelt" : ""}`}
        aria-expanded={offen}
        aria-label={ungelesen ? t("meldungen.glockeMitZahl", { zahl: ungelesen }) : t("meldungen.glocke")}
        title={t("meldungen.glocke")}
        onClick={() => setzeOffen((o) => !o)}
      >
        <Zeichen name="glocke" />
        {ungelesen ? <span className={`glocke-zahl${data?.blockiert ? " stopp" : ""}`}>{ungelesen > 99 ? "99+" : ungelesen}</span> : null}
      </button>
      {offen ? createPortal(
        <div ref={blatt} className="glocke-blatt" role="dialog" aria-label={t("meldungen.glocke")}
          style={{ top: platz.top, right: platz.right }}>
          <div className="glocke-kopf">
            <b>{t("meldungen.titel")}</b>
            <span className="glocke-kopf-knoepfe">
              {ungelesen ? (
                <Knopf klein art="stumm" onClick={() => gelesen.mutate(undefined)}>{t("meldungen.alleGelesen")}</Knopf>
              ) : null}
              <Knopf klein art="stumm" aria-pressed={fuerMich} onClick={() => setzeFuerMich((x) => !x)}>
                {t("meldungen.fuerMich")}
              </Knopf>
              {/* Am Telefon deckt das Blatt die Maske — es braucht einen Weg zu. */}
              <button type="button" className="glocke-zu" aria-label={t("allgemein.schliessen")} onClick={() => setzeOffen(false)}>×</button>
            </span>
          </div>
          {fuerMich ? <FuerMich /> : null}
          <div className="glocke-liste">
            {/* „Nichts" erst mit einer Antwort (N11-01, `daten/ladestand.ts`). */}
            {eintraege.length === 0 ? <p className="glocke-leer">{t(leersatzFuer(ladestandVon(meldungen), "meldungen.leer"))}</p> : null}
            {jetzt.length ? <div className="glocke-tag">{t("meldungen.heute")}</div> : null}
            {jetzt.map((m) => <Eintrag key={m.id} m={m} schliessen={() => setzeOffen(false)} />)}
            {frueher.length ? <div className="glocke-tag">{t("meldungen.frueher")}</div> : null}
            {frueher.map((m) => <Eintrag key={m.id} m={m} schliessen={() => setzeOffen(false)} />)}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
